import {
    GridMenuButton,
    GridMenuItem,
} from '@/global/components/DataGrid/components/CustomGridToolBar/MenuComponents'
import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import useDefaultFilterModel from '@/global/components/DataGrid/hooks/useDefaultFilterModel'
import useDefaultSortModel from '@/global/components/DataGrid/hooks/useDefaultSortModel'
import FilterModelPro from '@/global/components/DataGrid/utils/filterModelPro'
import { GridColDefPro } from '@/global/components/DataGrid/utils/types'
import Menu from '@/global/components/MenuV2'
import { useFilterLocaisCentroStore } from 'global/components/Selects'
import ContentGuide from 'global/guides/ContentGuide'
import { hasPermission } from 'hooks/useBackendMenu'
import {
    ArrowDownToLineIcon,
    ArrowUpDownIcon,
    ArrowUpToLineIcon,
    KeyboardIcon,
    PencilIcon,
    ScanFaceIcon,
    SettingsIcon,
} from 'lucide-react'
import { Outlet, useOutletContext } from 'react-router-dom'
import { Funcionario, funcionario, TabelaHorarios } from 'service/api'
import { useStore } from 'store'
import {
    columnsFiltrosFuncionarios,
    columnsFuncionarios,
} from 'templates/Cadastro/Funcionarios/constants/columns'

export default function Funcionarios() {
    const [filterModel, setFilterModel] = useDefaultFilterModel({
        items: [
            {
                columnField: 'dtRescisao',
                operatorValue: 'isEmpty',
                value: '',
            },
        ],
    })
    const empresaId = useStore(state => state.companyId)
    const setEmployeeId = useStore(state => state.setEmployeeId)
    const { data = [], isLoading } = funcionario.useFetch({
        queryParams: { empresaId },
    })

    const columns: GridColDefPro<Funcionario>[] = [
        ...columnsFuncionarios,
        ...columnsFiltrosFuncionarios,
        {
            ...optionsColumn,
            field: '__opcoes',
            type: 'actions',
            getActions: ({ id }) => [
                <Menu.Item href={`${id}/detalhes`} key="detalhes">
                    <Menu.Item.Icon>
                        <SettingsIcon />
                    </Menu.Item.Icon>
                    <Menu.Item.Text>Detalhes</Menu.Item.Text>
                </Menu.Item>,
                hasPermission(['update', 'Funcionários'], () => (
                    <Menu.Item href={`${id}/editar`} key="editar">
                        <Menu.Item.Icon>
                            <PencilIcon />
                        </Menu.Item.Icon>
                        <Menu.Item.Text>Editar</Menu.Item.Text>
                    </Menu.Item>
                )),
                hasPermission(['update', 'Funcionários'], () => (
                    <Menu.Item href={`${id}/cadastro-facial`} key="facial">
                        <Menu.Item.Icon>
                            <ScanFaceIcon />
                        </Menu.Item.Icon>
                        <Menu.Item.Text>Cadastro facial</Menu.Item.Text>
                    </Menu.Item>
                )),
                hasPermission(['read', 'Digitação'], () => (
                    <Menu.Item
                        href="/manutencao-ponto/digitacao"
                        key="digitacao"
                    >
                        <Menu.Item.Icon>
                            <KeyboardIcon />
                        </Menu.Item.Icon>
                        <Menu.Item.Text>Digitação</Menu.Item.Text>
                    </Menu.Item>
                )),
            ],
        },
    ]
    const [sortModel, setSortModel, sort] = useDefaultSortModel(columns)

    const filterLocaisCentro = useFilterLocaisCentroStore(item => item)
    const rows = data.filter(filterLocaisCentro)

    return (
        <ContentGuide>
            <ContentGuide.Card>
                <ContentGuide.Header label={['Cadastros', 'Funcionários']}>
                    <ContentGuide.Filtros
                        SelectEmpresas
                        SelectCentroCusto
                        SelectLocais
                    />
                </ContentGuide.Header>
                <ContentGuide.DataGrid
                    {...{ rows, columns, filterModel, sortModel }}
                    loading={isLoading}
                    onFilterModelChange={setFilterModel}
                    onSortModelChange={setSortModel}
                    onAdd
                    ToolbarProps={{
                        showVisibilityButton: true,
                        hideExportButton: true,
                        showQuickFilter: true,
                    }}
                    components={{
                        Toolbar: () => (
                            <GridMenuButton
                                startIcon={<ArrowUpDownIcon />}
                                label="Integração"
                            >
                                {hasPermission([
                                    'read',
                                    'ExportarFuncionários',
                                ]) && (
                                    <GridMenuItem
                                        startIcon={
                                            <ArrowUpToLineIcon className="mr-2" />
                                        }
                                        label="Exportar"
                                        href="exportar"
                                    />
                                )}
                                {hasPermission([
                                    'read',
                                    'ImportarFuncionários',
                                ]) && (
                                    <GridMenuItem
                                        startIcon={
                                            <ArrowDownToLineIcon className="mr-2" />
                                        }
                                        label="Importar"
                                        href="importar"
                                    />
                                )}
                            </GridMenuButton>
                        ),
                    }}
                    onRowClick={value => {
                        setEmployeeId(value.row.id)
                    }}
                />
            </ContentGuide.Card>
            <Outlet
                context={{
                    data: sort(
                        rows?.filter(
                            new FilterModelPro(filterModel, columns).filter,
                        ),
                    ),
                }}
            />
        </ContentGuide>
    )
}

export function useFuncionariosOutletContext() {
    return useOutletContext<{
        data?: Array<
            Funcionario & {
                TabelaHorario?: TabelaHorarios
                temAjustesPendentes?: boolean
            }
        >
    }>()
}
